import { useEffect, useRef } from 'react'

/**
 * Le compteur animé des tuiles — TRANSPOSITION de `animateStats`.
 *
 * ⚠️ Absent du portage, dans les DEUX consoles. C'est pourtant lui qui donne
 * leur vie aux chiffres : chaque valeur monte de zéro jusqu'à son total en
 * 750 ms. Sans lui les tuiles s'affichent figées, et l'écran paraît mort même
 * quand tout le reste est juste — une bonne part du « ça a régressé ».
 */
const DUREE = 750

/** Espace fine insécable comme séparateur de milliers, comme `fmt`. */
const grouper = (n: number) => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

export function Compteur({ valeur }: { valeur: string }) {
  const hote = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const el = hote.current
    if (!el) return

    /* On sépare le NOMBRE de ce qui l'entoure : « 12 500 FCFA » s'anime sur
       12500 et garde « FCFA », « 0 min » garde « min ». Une valeur sans
       nombre — « — » — n'est pas animée du tout. */
    const m = /^(-?[\d\s .,]+)(.*)$/.exec(valeur.trim())
    if (!m) {
      el.textContent = valeur
      return
    }
    const brut = m[1]!.replace(/[\s ]/g, '').replace(',', '.')
    const cible = parseFloat(brut)
    if (Number.isNaN(cible)) {
      el.textContent = valeur
      return
    }
    const decimales = (brut.split('.')[1] ?? '').length
    const suffixe = m[2] ?? ''

    let arret = false
    const depart = performance.now()
    const pas = (maintenant: number) => {
      if (arret) return
      /* Ease-out cubique : le chiffre part vite et se pose doucement. Une
         progression linéaire donne l'impression d'un compteur mécanique. */
      const p = 1 - Math.pow(1 - Math.min(1, (maintenant - depart) / DUREE), 3)
      const v = cible * p
      el.textContent = (decimales ? v.toFixed(decimales) : grouper(Math.round(v))) + suffixe
      if (p < 1) requestAnimationFrame(pas)
      // On repose la valeur EXACTE à la fin : l'arrondi de l'animation
      // pourrait afficher 12 499 pour 12 500.
      else el.textContent = valeur
    }
    requestAnimationFrame(pas)
    return () => {
      arret = true
    }
  }, [valeur])

  return <span ref={hote}>{valeur}</span>
}
